import type { Dayjs } from 'dayjs';
import { z } from 'zod';

/**
 * Zod schema for the 訪問追加 (AddVisitCard) form.
 *
 * Field-level checks here only do type guards (so the underlying parse never
 * aborts); all real validation lives in the `superRefine` block below — that
 * keeps every rule running on every submit, even when one field is missing.
 *
 * Conditional rule: when 突発的な往療 is checked, the medical-coordination
 * block (医療機関名 / 医師名 / 突発往療理由 / 連携日) becomes required.
 */
export const massagerAddVisitFormSchema = z
  .object({
    visit_time: z.string().default(''),
    duration: z
      .number({
        required_error: '所要時間を正しく入力してください。',
        invalid_type_error: '所要時間を正しく入力してください。',
      })
      .nullable()
      .optional(),
    clinic_location_id: z.number().optional(),
    patient_id: z.number().optional(),
    address_id: z.number().optional(),
    no_treatment: z.boolean().default(false),
    is_sudden: z.boolean().default(false),
    medical_institution_name: z.string().default(''),
    doctor_name: z.string().default(''),
    sudden_reason: z.string().default(''),
    coordination_date: z
      .custom<Dayjs | null>(v => v === null || v === undefined || typeof v === 'object', {
        message: '連携日を入力してください。',
      })
      .nullable()
      .default(null),
  })
  .superRefine((data, ctx) => {
    // visit_time
    if (!/^\d{2}:\d{2}$/.test(data.visit_time)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['visit_time'],
        message: '日付を正しく入力してください。',
      });
    }

    // duration
    if (data.duration == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['duration'],
        message: '所要時間を正しく入力してください。',
      });
    } else if (!Number.isInteger(data.duration) || data.duration < 1 || data.duration > 600) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['duration'],
        message: '所要時間を正しく入力してください。',
      });
    }

    // Cross-day: visit_datetime_to (start + duration) must stay on the same date
    // as visit_datetime_from. Computed in minutes from 00:00; > 1440 means the
    // visit spills into the next day.
    const timeMatch = data.visit_time.match(/^(\d{2}):(\d{2})$/);
    if (timeMatch && typeof data.duration === 'number' && data.duration > 0) {
      const startMinutes = parseInt(timeMatch[1], 10) * 60 + parseInt(timeMatch[2], 10);
      const endMinutes = startMinutes + data.duration;
      if (endMinutes > 1440) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['duration'],
          message: '日付をまたいだ施術日は登録できません',
        });
      }
    }

    // clinic_location_id
    if (data.clinic_location_id == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clinic_location_id'],
        message: '施設を選択してください',
      });
    }

    // patient_id — 0 is the 「選択してください」 placeholder sentinel from the
    // multi-result branch of search; treat it the same as missing.
    if (data.patient_id == null || data.patient_id === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['patient_id'],
        message: '受診者を選択してください。',
      });
    }

    // address_id — required once a patient is picked. Spec wires the message
    // to "受診者を選択してください。" since selecting a patient is what loads
    // the address list to begin with.
    if (data.address_id == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['address_id'],
        message: '受診者を選択してください。',
      });
    }

    // 突発的な往療 — conditional fields
    if (!data.is_sudden) return;
    if (!data.medical_institution_name.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['medical_institution_name'],
        message: '医療機関名を入力してください。',
      });
    }
    if (!data.doctor_name.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['doctor_name'],
        message: '医師名を入力してください。',
      });
    }
    if (!data.sudden_reason.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sudden_reason'],
        message: '突発往療理由を入力してください。',
      });
    }
    if (!data.coordination_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['coordination_date'],
        message: '連携日を入力してください。',
      });
    }
  });

export type MassagerAddVisitFormData = z.infer<typeof massagerAddVisitFormSchema>;
